// AddDatabaseModal.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Hooks
import useAddDatabaseModalState from './useAddDatabaseModalState';

// CSS
import './AddDatabaseModal.css';

// Components
import AddDatabaseModalCreateFlow from './AddDatabaseModalCreateFlow';
import AddDatabaseModalFooter from './AddDatabaseModalFooter';
import AddDatabaseModalLandingStep from './AddDatabaseModalLandingStep';
import AddDatabaseModalTemplateStep from './AddDatabaseModalTemplateStep';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import { LEGACY_LIBRARY_UI_ENABLED } from 'legacy/legacyFeatureFlags';

/**
 * AddDatabaseModal
 *
 * Wizard modal for adding a new database to the space.
 * When legacy library UI is enabled, also offers template provisioning from `_library`.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Called when the modal is dismissed.
 * @param {function} [onCreated] - Called when the modal closes after a database was created.
 * @param {string|null} baseKey - Active space key.
 *
 * @returns {ReactNode} The rendered modal.
 */
const AddDatabaseModal = React.memo(({
    isOpen,
    onClose,
    onCreated,
    baseKey,
}) => {

    // ----- HOOKS ----- //

    const {
        flowState,
        libraryState,
        createTypeState,
        modalActions,
    } = useAddDatabaseModalState({
        isOpen,
        onClose,
        onCreated,
        baseKey,
    });

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- RENDER ----- //

    return (
        <Modal
          iconKey="database"
          isOpen={isOpen}
          title={flowState.modalTitle}
          width={isMobile ? '100%' : 480}
          height="auto"
          maxHeight="90vh"
          onClose={modalActions.handleClose}
        >
            <ModalForm>
                <div className="add-database-modal-container">
                    {LEGACY_LIBRARY_UI_ENABLED && flowState.isLandingStep ? (
                        <AddDatabaseModalLandingStep
                          onOpenCreateFlow={modalActions.openCreateFlow}
                          onOpenTemplateFlow={modalActions.openTemplateFlow}
                        />
                    ) : null}

                    {LEGACY_LIBRARY_UI_ENABLED && flowState.isTemplateStep ? (
                        <AddDatabaseModalTemplateStep libraryState={libraryState} />
                    ) : null}

                    {flowState.isCreateFlow && (
                        <AddDatabaseModalCreateFlow
                          flowState={flowState}
                          createTypeState={createTypeState}
                          baseKey={baseKey}
                        />
                    )}

                </div>
            </ModalForm>

            <AddDatabaseModalFooter
              flowState={flowState}
              libraryState={libraryState}
              createTypeState={createTypeState}
              modalActions={modalActions}
            />

        </Modal>
    );
});

// Dev Tools
AddDatabaseModal.displayName = 'AddDatabaseModal';

export default AddDatabaseModal;
